import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useKitchensink } from '../context'
import { ingestBaseUrl } from '../env'
import { theme } from '../theme'

type ToggleState = 'enabled' | 'disabled'

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const stepNum: CSSProperties = {
  flexShrink: 0,
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: theme.accentSubtle,
  border: `1px solid ${theme.accent}`,
  color: theme.accent,
  fontSize: '11px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

export const TrackingTogglePage = () => {
  const { env } = useKitchensink()
  const [state, setState] = useState<ToggleState>('enabled')

  const ingestHost = new URL(ingestBaseUrl(env)).host

  const handleDisable = () => {
    window.ChiliWebData?.disableTracking()
    setState('disabled')
  }

  const handleEnable = () => {
    window.ChiliWebData?.enableTracking()
    setState('enabled')
  }

  const isEnabled = state === 'enabled'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: theme.accentSubtle,
            padding: '3px 8px',
            borderRadius: '4px',
          }}
        >
          Customer API
        </span>
      </div>

      <h1
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme.text,
          margin: '0 0 6px',
          letterSpacing: '-0.02em',
        }}
      >
        🔁 Tracking Toggle
      </h1>
      <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
        Test {code('enableTracking()')} / {code('disableTracking()')} on{' '}
        {code('window.ChiliWebData')}. These are independent of consent — customer controls.
      </p>

      {/* Controls */}
      <div style={card}>
        <p style={sectionTitle}>Controls</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            data-test-id='disable-tracking'
            onClick={handleDisable}
            disabled={state === 'disabled'}
            style={{
              backgroundColor: state === 'disabled' ? theme.surfaceElevated : '#7f1d1d',
              color: state === 'disabled' ? theme.textDim : '#fca5a5',
              border: `1px solid ${state === 'disabled' ? theme.border : '#991b1b'}`,
              borderRadius: '7px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: state === 'disabled' ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            Disable Tracking
          </button>

          <button
            data-test-id='enable-tracking'
            onClick={handleEnable}
            disabled={state === 'enabled'}
            style={{
              backgroundColor: state === 'enabled' ? theme.surfaceElevated : '#14532d',
              color: state === 'enabled' ? theme.textDim : '#86efac',
              border: `1px solid ${state === 'enabled' ? theme.border : '#166534'}`,
              borderRadius: '7px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: state === 'enabled' ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
          >
            Enable Tracking
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: isEnabled ? theme.success : '#f87171',
              backgroundColor: isEnabled ? theme.successSubtle : 'rgba(248, 113, 113, 0.1)',
              padding: '5px 12px',
              borderRadius: '6px',
              border: `1px solid ${isEnabled ? 'rgba(34, 197, 94, 0.2)' : 'rgba(248, 113, 113, 0.2)'}`,
            }}
          >
            <span>{isEnabled ? '●' : '○'}</span>
            Tracking {isEnabled ? 'enabled' : 'disabled'}
          </div>
        </div>
      </div>

      {/* Steps */}
      <div style={card}>
        <p style={sectionTitle}>Test steps</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[
            {
              step: '1',
              text: (
                <>
                  Click <strong style={{ color: theme.text }}>🔥 Initialize</strong> in the header
                  bar. Wait for the "Snippet initialized" confirmation.
                </>
              ),
            },
            {
              step: '2',
              text: (
                <>
                  Navigate to <strong style={{ color: theme.text }}>Page A</strong> or{' '}
                  <strong style={{ color: theme.text }}>Page B</strong>. Verify{' '}
                  {code(`${ingestHost}/api/s/page`)} fires in the Network tab.
                </>
              ),
            },
            {
              step: '3',
              text: (
                <>
                  Come back here and click{' '}
                  <strong style={{ color: '#fca5a5' }}>Disable Tracking</strong>. Navigate to Page A
                  or B again — no {code('/api/s/page')} request should appear. Engagement heartbeat
                  also stops.
                </>
              ),
            },
            {
              step: '4',
              text: (
                <>
                  Click <strong style={{ color: '#86efac' }}>Enable Tracking</strong>. A new page
                  view fires <em>immediately</em> — no navigation needed. Verify{' '}
                  {code('/api/s/page')} and {code('/api/s/identify')} appear in the Network tab.
                </>
              ),
            },
            {
              step: '5',
              text: (
                <>
                  Disable tracking again, then navigate between pages. Confirm the form observer and
                  file download listeners are also inactive (no {code('/api/s/track')} events for
                  those actions).
                </>
              ),
            },
          ].map(({ step, text }) => (
            <div key={step} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={stepNum}>{step}</div>
              <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted, lineHeight: '1.6' }}>
                {text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
